<template>
  <div
    class="onboarding-form-field"
    :class="{ 'onboarding-form-field--error': Boolean(error) }"
    @focusout="markFieldTouched(fieldKey)"
  >
    <div class="onboarding-field-label-row">
      <label
        class="text-body-2 font-weight-medium"
        :class="icon ? 'd-flex align-center ga-2' : 'd-block'"
      >
        <v-icon
          v-if="icon"
          size="16"
          color="primary"
        >
          {{ icon }}
        </v-icon>
        <span>
          {{ label }}
          <span
            v-if="required"
            class="text-error"
            aria-hidden="true"
            >*</span
          >
        </span>
      </label>
      <div class="onboarding-field-badge-slot">
        <AiBadge
          v-if="captured"
          :label="$t('onboarding.wizard.data-entry.ai-captured')"
        />
      </div>
    </div>
    <slot />
    <div
      v-if="error"
      class="onboarding-form-field__error text-caption text-error mt-1"
      role="alert"
    >
      {{ error }}
    </div>
    <p
      v-if="hint"
      class="onboarding-form-field__hint text-caption text-medium-emphasis mt-1 mb-0"
    >
      {{ hint }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import AiBadge from '../../AiBadge.vue'
import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'

const props = defineProps<{
  fieldKey: string
  label: string
  icon?: string
  /** Helper text shown below the field. */
  hint?: string
}>()

const { aiCapturedFields, visibleFieldError, markFieldTouched, isFieldRequired } =
  useOnboardingDataEntryContext()
const required = computed(() => isFieldRequired(props.fieldKey))
const captured = computed(() => aiCapturedFields.value.has(props.fieldKey))
const error = computed(() => visibleFieldError(props.fieldKey))
</script>

<style scoped>
.onboarding-form-field--error :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  color: rgb(var(--v-theme-error));
}

/* Reserve space for optional “AI captured” badge so paired columns (e.g. city / zip) stay aligned */
.onboarding-field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.onboarding-field-badge-slot {
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}
</style>
